import React, { Dispatch, SetStateAction } from "react"
import EditHalfScreen from "../../Ui/EditHalfScreen/EditHalfScreen"
import EditSidebarHeader from "../../Ui/EditSidebar/EditSidebarHeader"
import {
  CustomReasonCode,
  Maybe,
  OrderOperationSearchHit,
  OrderOperationSearchHits,
  OrderReleaseSearchHit
} from "lib/types/generated/graphql-types"
import DeliveryAction from "./DeliveryAction"
import CompensationAction from "./CompensationAction"
import { ApolloQueryResult, useQuery } from "@apollo/client"
import GET_ORDER from "../../../graphql/queries/order/GetOrder"
import ALL_REASON_CODES from "../../../graphql/queries/settings/reasonCodes/AllReasonCodes"
import RefundAction from "./RefundAction"
import CancellationAction from "./CancellationAction"
import ReleaseAction from "./ReleaseAction"
import OrderLinesModificationAction from "./OrderLinesModificationAction"

type Props = {
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
  operation: OrderOperationSearchHit
  refetchOperations: () => Promise<ApolloQueryResult<OrderOperationSearchHits>>
  setCreatedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  setFailedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  getOperationConfig: (operation: string) =>
    | {
        icon: React.JSX.Element
        color: string
        label: string
      }
    | undefined
}

const OperationActions = ({
  setShow,
  operation,
  refetchOperations,
  setCreatedOperations,
  setFailedOperations,
  getOperationConfig
}: Props) => {
  const {
    data: orderData,
    loading: orderLoading,
    refetch
  } = useQuery(GET_ORDER, {
    variables: { id: operation.orderId },
    fetchPolicy: "no-cache"
  })
  const { data: reasonData, loading: reasonLoading } = useQuery(ALL_REASON_CODES)
  const customReasons = reasonData?.Order?.customReasonCodes?.codes

  const getCustomCode = (code: Maybe<string> | undefined) =>
    customReasons?.find((customCode: CustomReasonCode) => customCode.code === code)

  const getOperation = () => {
    switch (operation.type) {
      case "Delivery":
        return (
          <DeliveryAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            refetchOperations={refetchOperations}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
          />
        )
      case "Compensation":
        return (
          <CompensationAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
          />
        )
      case "Refund":
        return (
          <RefundAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            refetchOrder={refetch}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
          />
        )
      case "Release":
        return (
          <ReleaseAction
            operation={operation as OrderReleaseSearchHit}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            refetchOrder={refetch}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
          />
        )
      case "Cancellation":
        return (
          <CancellationAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
          />
        )
      case "OrderLinesModification":
        return (
          <OrderLinesModificationAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            refetchOperations={refetchOperations}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
          />
        )
    }
  }

  if (orderLoading || reasonLoading) {
    return (
      <EditHalfScreen loading={orderLoading || reasonLoading} cancelEvent={() => setShow(false)}>
        <EditSidebarHeader title={`Handle ${getOperationConfig(operation.type)?.label}`} cancelEvent={() => setShow(false)} />
      </EditHalfScreen>
    )
  }

  return (
    <>
      <EditHalfScreen cancelEvent={() => setShow(false)}>
        <EditSidebarHeader
          title={`Handle ${getOperationConfig(operation.type)?.label}`}
          icon={getOperationConfig(operation.type)?.icon}
          id={operation.id}
          cancelEvent={() => setShow(false)}
          width={"100%"}
        />
        {getOperation()}
      </EditHalfScreen>
    </>
  )
}

export default OperationActions
